import {
  bulkTagsService,
  callFlowService,
  textToSpeechService
} from '@/angularBridgeCode/ringbaPortal.servicesModule';
import {
  ATTACH_NODE,
  CLONE_AND_SAVE_DRAFT,
  CONVERT_TEXT_TO_SPEECH,
  DELETE_CALL_FLOW_TREE,
  DUPLICATE_CALL_FLOW_TREE,
  GET_AVAILABLE_NODES,
  GET_AVAILABLE_TAGS,
  GET_AVAILABLE_TEMPLATES,
  GET_AVAILABLE_VOICES,
  GET_CALL_FLOW_TREE,
  GET_CALL_FLOW_TREE_REFERENCES,
  GET_CALL_FLOW_TREES,
  INITIALIZE_CALL_FLOW_TREE,
  INITIALIZE_CALL_FLOW_TREE_TEMPLATE,
  PROCESS_SCENE_ERRORS,
  RESET_TEXT_TO_SPEECH_STATE,
  SAVE_DRAFT,
  SAVE_TREE,
  UPDATE_NODE
} from './actionTypes';
import {
  SET_AVAILABLE_NODES,
  SET_AVAILABLE_TAGS,
  SET_AVAILABLE_TEMPLATES,
  SET_AVAILABLE_VOICES,
  SET_CALL_FLOW_SCENE_ERRORS,
  SET_CALL_FLOW_TREE,
  SET_CALL_FLOW_TREE_REFERENCES,
  SET_CALL_FLOW_TREES
} from './mutationTypes';

import {
  SET_ACTION_STATUS_ERROR,
  SET_ACTION_STATUS_PENDING,
  SET_ACTION_STATUS_SUCCESS
} from '@/store/helpers';

import { rootNode } from '../helpers/constants';
import { beautyfyLegacyIVRTree, isLegacyIVRTree } from '../helpers/legacyIVR';
import { sanitizeNodeSettings } from '../helpers/save';

/**
 * @type {import('vuex').ActionTree<typeof state>}
 */
const actions = {
  async [GET_AVAILABLE_NODES]({ rootState, commit, dispatch }) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: GET_AVAILABLE_NODES });
    commit(SET_AVAILABLE_NODES, []);
    dispatch('setNodesValidationMapping');

    try {
      const availableNodes = await callFlowService.getAvailableNodes(rootState.currentAccountId, {}, 'root');

      availableNodes.push(rootNode);

      commit(SET_AVAILABLE_NODES, availableNodes);
      dispatch('setNodesValidationMapping');
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: GET_AVAILABLE_NODES });
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: GET_AVAILABLE_NODES, error: e });
    }
  },
  async [GET_AVAILABLE_TAGS]({ rootState, commit }, payload) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: GET_AVAILABLE_TAGS });
    commit(SET_AVAILABLE_TAGS, []);

    try {
      payload.tree.accountId = rootState.currentAccountId;
      const availableTags = await callFlowService.getAvailableTags(rootState.currentAccountId, payload.tree, payload.parentNodeId);
      commit(SET_AVAILABLE_TAGS, availableTags);
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: GET_AVAILABLE_TAGS });
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: GET_AVAILABLE_TAGS, error: e });
    }
  },
  async [GET_AVAILABLE_TEMPLATES]({ rootState, commit }) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: GET_AVAILABLE_TEMPLATES });
    commit(SET_AVAILABLE_TEMPLATES, []);
    try {
      const availableTemplates = await callFlowService.getAvailableTemplates(rootState.currentAccountId, {}, 'root');
      commit(SET_AVAILABLE_TEMPLATES, availableTemplates);
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: GET_AVAILABLE_TEMPLATES });
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: GET_AVAILABLE_TEMPLATES, error: e });
    }
  },
  async [GET_AVAILABLE_VOICES]({ rootState, commit }) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: GET_AVAILABLE_VOICES });
    commit(SET_AVAILABLE_VOICES, []);
    try {
      const availableVoices = await textToSpeechService.getAvailableVoices(rootState.currentAccountId);
      commit(SET_AVAILABLE_VOICES, availableVoices.data);
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: GET_AVAILABLE_VOICES });
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: GET_AVAILABLE_VOICES, error: e });
    }
  },
  async [GET_CALL_FLOW_TREES]({ rootState, commit }) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: GET_CALL_FLOW_TREES });
    commit(SET_CALL_FLOW_TREES, []);
    try {
      const callFlowTrees = await callFlowService.getCallFlowTrees(rootState.currentAccountId, {}, 'root');
      commit(SET_CALL_FLOW_TREES, callFlowTrees.callFlows);
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: GET_CALL_FLOW_TREES });
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: GET_CALL_FLOW_TREES, error: e });
    }
  },
  async [GET_CALL_FLOW_TREE]({ rootState, commit, dispatch }, treeId) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: GET_CALL_FLOW_TREE });
    commit(SET_CALL_FLOW_SCENE_ERRORS, []);
    try {
      const callFlowTree = await callFlowService.getCallFlowTree(rootState.currentAccountId, treeId);

      let callFlow = callFlowTree.data.callFlow;

      if (isLegacyIVRTree(callFlow)) {
        callFlow = beautyfyLegacyIVRTree(callFlow);
      }

      dispatch('prepareScene', callFlow);

      commit(SET_CALL_FLOW_TREE, callFlow);

      commit(SET_ACTION_STATUS_SUCCESS, { actionName: GET_CALL_FLOW_TREE });

      return callFlow;
    } catch (e) {
      console.error(e);
      commit(SET_ACTION_STATUS_ERROR, { actionName: GET_CALL_FLOW_TREE, error: e });
      commit(SET_CALL_FLOW_SCENE_ERRORS, [{ errorMessage: e.data.errors[0] }]);
    }
  },
  async [INITIALIZE_CALL_FLOW_TREE]({ rootState, commit, dispatch }) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: INITIALIZE_CALL_FLOW_TREE });
    commit(SET_CALL_FLOW_SCENE_ERRORS, []);
    try {
      const callFlowTree = await callFlowService.initializeCallFlowTree(rootState.currentAccountId, 'New Call Flow');

      const callFlow = callFlowTree.data.callFlow;

      dispatch('prepareScene', callFlow);

      commit(SET_ACTION_STATUS_SUCCESS, { actionName: INITIALIZE_CALL_FLOW_TREE });
      return callFlow;
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: INITIALIZE_CALL_FLOW_TREE, error: e });
    }
  },

  async [INITIALIZE_CALL_FLOW_TREE_TEMPLATE]({ rootState, commit, dispatch }, payload) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: INITIALIZE_CALL_FLOW_TREE_TEMPLATE });
    commit(SET_CALL_FLOW_SCENE_ERRORS, []);
    try {
      const newCallFlowTree = await callFlowService.initializeCallFlowTreeTemplate(rootState.currentAccountId, payload.templateId, payload.name);

      let callFlow = newCallFlowTree.data.ivrTree;

      if (isLegacyIVRTree(callFlow)) {
        callFlow = beautyfyLegacyIVRTree(callFlow);
      }

      dispatch('prepareScene', callFlow);

      commit(SET_ACTION_STATUS_SUCCESS, { actionName: INITIALIZE_CALL_FLOW_TREE_TEMPLATE });
      return callFlow;
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: INITIALIZE_CALL_FLOW_TREE_TEMPLATE, error: e });
    }
  },
  async [DUPLICATE_CALL_FLOW_TREE]({ rootState, commit }, treeId) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: DUPLICATE_CALL_FLOW_TREE });
    commit(SET_CALL_FLOW_SCENE_ERRORS, []);
    try {
      const newCallFlowTree = await callFlowService.duplicateCallFlowTree(rootState.currentAccountId, treeId);
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: DUPLICATE_CALL_FLOW_TREE });
      let callFlow = newCallFlowTree.data.callFlow;
      if (isLegacyIVRTree(callFlow)) {
        callFlow = beautyfyLegacyIVRTree(callFlow);
      }
      return callFlow;
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: DUPLICATE_CALL_FLOW_TREE, error: e });
      throw e;
    }
  },
  async [ATTACH_NODE]({ rootState, commit }, payload) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: ATTACH_NODE });
    try {
      const sanitizedNode = payload.node;
      sanitizedNode.settings = sanitizeNodeSettings(sanitizedNode.settings);
      const attachNode = await callFlowService.attachNode(rootState.currentAccountId, sanitizedNode, payload.name, payload.parentNodeId, payload.tree, payload.onSuccess, payload.criteria);
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: ATTACH_NODE });
      return attachNode.data;
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: ATTACH_NODE, error: e });
      // Temporary, propose that the error messages be returned in sentence form to avoid this
      if (e.data !== null && e.data.nodeErrors !== null) {
        e.data.nodeErrors.forEach((e) => {
          if (e.errors) {
            for (let i = 0; i < e.errors.length; i++) {
              const errorMessage = e.errors[i];
              e.errors[i] = errorMessage.charAt(0).toUpperCase() + errorMessage.substr(1) + '.';
            }
          }
        });
      }
      // End temp
      throw e;
    }
  },

  async [CLONE_AND_SAVE_DRAFT]({ rootState, commit, dispatch }, tree) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: CLONE_AND_SAVE_DRAFT });
    tree.accountId = rootState.currentAccountId;
    tree.name = tree.name + ' - Copy';

    try {
      const newTree = await callFlowService.initializeCallFlowTree(rootState.currentAccountId, tree.name);
      tree.id = newTree.data.callFlow.id;

      const result = await dispatch(SAVE_DRAFT, tree);

      commit(SET_ACTION_STATUS_SUCCESS, { actionName: CLONE_AND_SAVE_DRAFT });
      return result;
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: CLONE_AND_SAVE_DRAFT, error: e });
    }
  },

  async [SAVE_DRAFT]({ rootState, commit, dispatch }, tree) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: SAVE_DRAFT });
    tree.accountId = rootState.currentAccountId;

    try {
      const result = await callFlowService.saveDraftTree(rootState.currentAccountId, tree);

      dispatch('prepareScene', result.data.ivrTree);

      commit(SET_ACTION_STATUS_SUCCESS, { actionName: SAVE_DRAFT });
      return result.data;
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: SAVE_DRAFT, error: e });
    }
  },

  async [SAVE_TREE]({ rootState, commit, dispatch }, tree) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: SAVE_TREE });
    tree.accountId = rootState.currentAccountId;

    try {
      const result = await callFlowService.commitTree(rootState.currentAccountId, tree);

      dispatch('prepareScene', result.data.ivrTree);

      commit(SET_ACTION_STATUS_SUCCESS, { actionName: SAVE_TREE });
      return result.data;
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: SAVE_TREE, error: e });

      if (e.status === 400 && e.data !== null && e.data.nodeErrors !== null) {
        commit(SET_CALL_FLOW_SCENE_ERRORS, [{ errorMessage: e.data.errors[0].errorMessage }]);

        const callFlow = e.data.ivrTree;

        dispatch('prepareScene', callFlow);
      }
    }
  },

  async [UPDATE_NODE]({ rootState, commit }, payload) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: UPDATE_NODE });
    try {
      const sanitizedNode = payload.node;
      sanitizedNode.settings = sanitizeNodeSettings(sanitizedNode.settings);
      // TODO: this should be in another place, refactor code so it makes sense
      payload.tree.accountId = rootState.currentAccountId;
      const updateNode = await callFlowService.updateNode(rootState.currentAccountId, payload.tree, sanitizedNode);
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: UPDATE_NODE });
      return updateNode.data;
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: UPDATE_NODE, error: e });
      throw e;
    }
  },
  async [GET_CALL_FLOW_TREE_REFERENCES]({ rootState, commit }, treeId) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: GET_CALL_FLOW_TREE_REFERENCES });
    try {
      const callFlowTreeReferences = await callFlowService.getCallFlowTreeReferences(rootState.currentAccountId, treeId);
      // Timeout added to allow user to see loading message
      commit(SET_CALL_FLOW_TREE_REFERENCES, callFlowTreeReferences.data);
      setTimeout(() => {
        commit(SET_ACTION_STATUS_SUCCESS, { actionName: GET_CALL_FLOW_TREE_REFERENCES });
      }, 1000);
      return callFlowTreeReferences.data;
    } catch (e) {
      //DELETE_CALL_FLOW_MODAL_CHECK_ASSOCIATION_ERROR_TEXT
      commit(SET_CALL_FLOW_TREE_REFERENCES, {});
      commit(SET_ACTION_STATUS_ERROR, { actionName: GET_CALL_FLOW_TREE_REFERENCES, error: e });
    }
  },
  async [DELETE_CALL_FLOW_TREE]({ rootState, commit }, payload) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: DELETE_CALL_FLOW_TREE });
    try {
      await callFlowService.deleteCallFlowTree(rootState.currentAccountId, payload.treeId, payload.force);
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: DELETE_CALL_FLOW_TREE });
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: DELETE_CALL_FLOW_TREE, error: e });
    }
  },

  [PROCESS_SCENE_ERRORS]({ commit }, errors) {
    commit(SET_CALL_FLOW_SCENE_ERRORS, errors);
  },

  async [CONVERT_TEXT_TO_SPEECH]({ rootState, commit }, payload) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: CONVERT_TEXT_TO_SPEECH });
    try {
      const response = await textToSpeechService.convertTextToSpeech(rootState.currentAccountId, payload.voiceId, payload.providerId, payload.text);
      commit('setNewTextToSpeechRecordingId', response.data.textToSpeechRecordingId);
      commit('setNewTextToSpeechRecordingUrl', response.data.recordingURL);
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: CONVERT_TEXT_TO_SPEECH });
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: CONVERT_TEXT_TO_SPEECH, error: e });
    }
  },

  async [RESET_TEXT_TO_SPEECH_STATE]({ commit }) {
    commit('setNewTextToSpeechRecordingId',null);
    commit('setNewTextToSpeechRecordingUrl', null);
  },

  // TODO: Move bulk tags to its own namespaced store
  async loadBulkTags({ commit, rootState }) {
    const response = await bulkTagsService.getBulkTags(rootState.currentAccountId);

    commit('setBulkTagUploads', response.data.bulkTagUploads?.sort((a, b) => {
      if (a.name < b.name) {
        return -1;
      }

      if (a.name > b.name) {
        return 1;
      }

      return 0;
    }));
  },

  async checkTagExistsInUploadedBulkTags({ commit, rootState }, { searchTag, selectedBulkTagId }) {
    commit('setSearchedTagExistsInBulkTag', null);

    if (!searchTag) {
      return;
    }

    try {
      commit(SET_ACTION_STATUS_PENDING, { actionName: 'checkTagExistsInUploadedBulkTags' });

      const response = await bulkTagsService.checkTagExistsInUpload(rootState.currentAccountId, searchTag, selectedBulkTagId);

      commit('setSearchedTagExistsInBulkTag', response.data.exists);

      commit(SET_ACTION_STATUS_SUCCESS, { actionName: 'checkTagExistsInUploadedBulkTags' });
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: 'checkTagExistsInUploadedBulkTags', error: e });
    }
  },

  async uploadBulkTagFile({ commit, rootState, dispatch }, file) {
    if (!file) {
      return;
    }

    try {
      commit(SET_ACTION_STATUS_PENDING, { actionName: 'uploadBulkTagFile' });

      const response = await bulkTagsService.uploadValues(rootState.currentAccountId, file);

      commit(SET_ACTION_STATUS_SUCCESS, { actionName: 'uploadBulkTagFile' });

      commit('setIsDuplicate', response.data.duplicateDetected || false);
      commit('setUploadedFileName', response.data.bulkTagUpload.name);

      if (!response.data.duplicateDetected) {
        await dispatch('loadBulkTags');
      }

      commit('setNewUploadedBulkTag', response.data.bulkTagUpload.id);
      return response.data;
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: 'uploadBulkTagFile', error: e });
    }
  }

};
export default actions;
